import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, LinearProgress, Link as MuiLink, Paper, Snackbar, Stack, Table, TableBody, TableCell, TableRow,
  TextField, Typography,
} from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import MiniMarkdown from '../components/MiniMarkdown';
import { useVoice } from '../VoiceContext';
import { useLang } from '../i18n';
import { speak as speakWith, voicesFor } from '../voice';
import { lettersOf, toLatin } from '../russianLetters';
import AlphabetTable from '../components/AlphabetTable';
import TableCard from '../components/TableCard';
import AuxCard from '../components/AuxCard';
import { useBugReport } from '../components/BugReport';
import {
  getCurrentCourse, getStudyProgress, getStudyQueue, overrideStudyRating,
  startStudySession, submitStudyAnswer, updateStudySession,
} from '../api';

const IDLE_LIMIT_MS = 90 * 1000; // без действий дольше 1.5 мин — таймер на паузе
const SAVE_EVERY_S = 20;
const keyOf = (c) => `${c.item_id}~${c.skill}`;

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const VERDICT_SEVERITY = { exact: 'success', translit: 'success', accent: 'warning', typo: 'warning', synonym: 'info', partial: 'warning', wrong: 'error' };

export default function Study() {
  const { voices } = useVoice();
  const { lang, t } = useLang();
  const { openBugReport } = useBugReport();
  const locale = lang === 'it' ? 'it-IT' : 'ru-RU';
  const [phase, setPhase] = useState('intro'); // intro | study | done
  const [progress, setProgress] = useState(null);
  const [course, setCourse] = useState(null);
  const [session, setSession] = useState(null);
  const [queue, setQueue] = useState([]);
  const [queueEmpty, setQueueEmpty] = useState(false);
  const [nextDue, setNextDue] = useState(null);
  const [answer, setAnswer] = useState('');
  const [tableAnswers, setTableAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [ratingNote, setRatingNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activeSeconds, setActiveSeconds] = useState(0);
  const [stats, setStats] = useState({ answered: 0, correct: 0 });
  const [grammarLesson, setGrammarLesson] = useState(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [goalToast, setGoalToast] = useState(false);
  const [introSeen, setIntroSeen] = useState(() => new Set());

  const lastActivity = useRef(Date.now());
  const activeRef = useRef(0);
  const goalReached = useRef(false);
  // таймер показывает время за весь сегодняшний день: уже набранное + текущая сессия
  const todayBase = useRef(0);
  const [todayBaseSeconds, setTodayBaseSeconds] = useState(0);
  const [searchParams] = useSearchParams();
  const lessonJump = useRef(searchParams.get('lesson') != null);
  const shownAt = useRef(Date.now());
  const inputRef = useRef(null);
  const loadingMore = useRef(false);

  const goalSeconds = (progress?.goal_minutes || 20) * 60;
  const todaySeconds = todayBaseSeconds + activeSeconds;
  const card = queue[0];

  useEffect(() => {
    getStudyProgress().then(setProgress).catch(() => setError(t('study.errLoadProgress')));
    getCurrentCourse().then(setCourse).catch(() => {});
  }, [t]);

  const touch = () => { lastActivity.current = Date.now(); };

  const saveSession = useCallback(async (finish = false) => {
    if (!session) return;
    try {
      await updateStudySession(session.id, activeRef.current, finish);
    } catch (e) {
      // таймер не критичен: следующая попытка сохранит актуальное значение
    }
  }, [session]);

  // Таймер активного времени
  useEffect(() => {
    if (phase !== 'study') return undefined;
    const timer = setInterval(() => {
      const visible = document.visibilityState === 'visible';
      if (!visible || Date.now() - lastActivity.current > IDLE_LIMIT_MS) return;
      activeRef.current += 1;
      setActiveSeconds(activeRef.current);
      if (activeRef.current % SAVE_EVERY_S === 0) saveSession();
      if (!goalReached.current && todayBase.current + activeRef.current >= goalSeconds) {
        goalReached.current = true;
        setGoalToast(true);
        saveSession();
      }
    }, 1000);
    const onHide = () => { if (document.visibilityState === 'hidden') saveSession(); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [phase, goalSeconds, saveSession]);

  const loadMore = useCallback(async (currentQueue, extraNew = 0) => {
    if (loadingMore.current) return;
    loadingMore.current = true;
    try {
      const data = await getStudyQueue({ limit: 15, exclude: currentQueue.map(keyOf), extraNew });
      setNextDue(data.next_due);
      if (data.cards.length) {
        setQueue((q) => {
          const known = new Set(q.map(keyOf));
          return [...q, ...data.cards.filter((c) => !known.has(keyOf(c)))];
        });
        setQueueEmpty(false);
      } else if (!currentQueue.length) {
        setQueueEmpty(true);
      }
    } catch (e) {
      setError(t('study.errCards'));
    } finally {
      loadingMore.current = false;
    }
  }, [t]);

  const start = async (extraNew = 0) => {
    setError('');
    setBusy(true);
    try {
      const [s, fresh] = await Promise.all([startStudySession(), getStudyProgress().catch(() => null)]);
      setSession(s);
      if (fresh) setProgress(fresh);
      todayBase.current = Math.round(((fresh || progress)?.minutes_today || 0) * 60);
      setTodayBaseSeconds(todayBase.current);
      activeRef.current = 0;
      goalReached.current = todayBase.current >= goalSeconds;  // цель уже выполнена сегодня — не поздравляем повторно
      setActiveSeconds(0);
      setStats({ answered: 0, correct: 0 });
      setQueue([]);
      touch();
      setPhase('study');
      // перешли из «Курса» кнопкой «Начать этот урок» — даём новые карточки сверх дневного лимита
      await loadMore([], lessonJump.current ? 10 : extraNew);
      lessonJump.current = false;
    } catch (e) {
      setError(t('study.errStart'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    shownAt.current = Date.now();
    if (card && !result) setTimeout(() => inputRef.current?.focus(), 50);
  }, [card, result, introSeen]);

  const speak = (text, target = 'it') => speakWith(voices, text, target);

  // Голос изучаемого языка: браузеры подгружают голоса с задержкой, поэтому проверяем через 3 секунды
  const studyTarget = course?.target || 'it';
  const [noVoice, setNoVoice] = useState(false);
  useEffect(() => {
    if (!window.speechSynthesis) { setNoVoice(true); return undefined; }
    if (voicesFor(voices, studyTarget).length > 0) { setNoVoice(false); return undefined; }
    const timer = setTimeout(() => setNoVoice(voicesFor(window.speechSynthesis.getVoices(), studyTarget).length === 0), 3000);
    return () => clearTimeout(timer);
  }, [voices, studyTarget]);
  const noVoiceAlert = noVoice && (
    <Alert severity="info" sx={{ mb: 2 }}>
      {t('study.noVoice')}{' '}<Link to="/voice-settings">{t('account.voice')}</Link>
    </Alert>
  );

  const submit = async (giveUp = false, picked = null) => {
    if (!card || busy) return;
    touch();
    setBusy(true);
    try {
      const r = await submitStudyAnswer({
        item_id: card.item_id,
        skill: card.skill,
        person: card.person ?? null,
        answer: giveUp ? '' : picked ?? (card.type === 'table' ? tableAnswers.join('\n') : answer),
        latency_ms: Date.now() - shownAt.current,
        session_id: session?.id,
      });
      setResult(r);
      setRatingNote('');
      setStats((s) => ({ answered: s.answered + 1, correct: s.correct + (r.correct ? 1 : 0) }));
      if (r.verdict !== 'exact' && !['table', 'aux'].includes(card.type)) speak(r.expected, card.target);
    } catch (e) {
      setError(t('study.errAnswer'));
    } finally {
      setBusy(false);
    }
  };

  const rerate = async (rating, note) => {
    if (!result) return;
    touch();
    try {
      await overrideStudyRating(result.attempt_id, rating);
      const wasCorrect = result.correct;
      const nowCorrect = rating > 1;
      if (wasCorrect !== nowCorrect) {
        setStats((s) => ({ ...s, correct: s.correct + (nowCorrect ? 1 : -1) }));
      }
      setResult({ ...result, correct: nowCorrect, rating });
      setRatingNote(note);
    } catch (e) {
      setError(t('study.errRating'));
    }
  };

  const next = () => {
    touch();
    const rest = queue.slice(1);
    setQueue(rest);
    setAnswer('');
    setTableAnswers([]);
    setResult(null);
    if (rest.length <= 3) loadMore(rest);
    if (!rest.length) setQueueEmpty(true);
  };

  const finish = async () => {
    setConfirmFinish(false);
    await saveSession(true);
    setPhase('done');
    getStudyProgress().then(setProgress).catch(() => {});
  };

  const askFinish = () => {
    if (todayBase.current + activeRef.current < goalSeconds) setConfirmFinish(true);
    else finish();
  };

  const onKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (result) next();
    else if (answer.trim()) submit();
  };

  const lessonData = (n) => course?.lessons?.find((l) => l.n === n);

  // ---------- экраны ----------

  if (phase === 'intro') {
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', px: 2 }}>
        <Typography variant="h4" gutterBottom>{t('study.title')}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {noVoiceAlert}
        {!progress ? <CircularProgress /> : (
          <Paper sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip label={t('study.streak', { n: progress.streak })} />
              <Chip label={t('study.due', { n: progress.totals.due_now })} />
              <Chip label={t('study.todayMinutes', { m: Math.round(progress.minutes_today), goal: progress.goal_minutes })} />
            </Stack>
            {progress.current_lesson && (
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('study.currentLesson', { n: progress.current_lesson.n, title: progress.current_lesson.title })}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('study.plan', {
                due: progress.totals.due_now,
                new: lessonJump.current ? progress.new_left_today + 10 : progress.new_left_today,
              })}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('study.goalText', { goal: progress.goal_minutes })}
            </Typography>
            {progress.new_left_today === 0 && !lessonJump.current && (
              <Alert severity="info" sx={{ mb: 2 }}
                action={<Button size="small" onClick={() => start(5)} disabled={busy}>{t('study.plusFive')}</Button>}>
                {t('study.noNewToday')}
              </Alert>
            )}
            <Button variant="contained" size="large" fullWidth onClick={() => start()} disabled={busy}>
              {t('study.start')}
            </Button>
            <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'center' }}>
              <Button component={Link} to="/progress" size="small">{t('study.toProgress')}</Button>
              <Button component={Link} to="/course" size="small">{t('study.toCourse')}</Button>
            </Stack>
          </Paper>
        )}
      </Box>
    );
  }

  if (phase === 'done') {
    const accuracy = stats.answered ? Math.round((stats.correct / stats.answered) * 100) : 0;
    return (
      <Box sx={{ maxWidth: 560, mx: 'auto', px: 2 }}>
        <Typography variant="h4" gutterBottom>
          {todaySeconds >= goalSeconds ? t('study.bravo') : t('study.endTitle')}
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography>{t('study.timeOf', { t: formatTime(todaySeconds), goal: Math.round(goalSeconds / 60) })}</Typography>
          <Typography>{t('study.answersOf', { n: stats.answered, p: accuracy })}</Typography>
          {progress && <Typography>{t('study.streakLine', { n: progress.streak })}</Typography>}
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => { setPhase('intro'); }}>{t('study.again')}</Button>
            <Button component={Link} to="/progress">{t('study.toProgress')}</Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  const pct = Math.min(100, (todaySeconds / goalSeconds) * 100);
  const isRead = card?.type === 'read';
  const verdictKey = result && isRead ? (result.correct ? 'study.readOk' : 'study.readWrong') : `study.verdict.${result?.verdict}`;
  const verdict = result ? { severity: VERDICT_SEVERITY[result.verdict], title: t(verdictKey, { verb: result.synonym, target: card?.infinitive }) } : null;
  // урок 0 — валидный номер, поэтому сравниваем с null, а не проверяем истинность
  const lesson = card?.lesson != null ? lessonData(card.lesson) : null;
  const target = card?.target || 'it';
  const promptLabel = card?.type === 'read' ? t('study.read')
    : card?.type === 'drill' ? t('study.drill') : t(`study.howToSay.${target}`);
  const showIntro = Boolean(card?.lesson_intro && lesson && !introSeen.has(lesson.n));
  // латиница для русских слов — тем, кто учит русский
  const showLatin = target === 'ru';
  // карточка из урока раньше текущего — предупреждаем, чтобы не путало
  const currentLessonN = progress?.current_lesson?.n;
  const oldLesson = card?.course !== 'personal' && card?.lesson != null && currentLessonN != null
    && card.lesson < currentLessonN;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', px: 2 }} onPointerDown={touch}>
      {/* шапка с таймером */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="body2">
            ⏱ {formatTime(todaySeconds)} / {Math.round(goalSeconds / 60)}:00
          </Typography>
          <Typography variant="body2">
            ✓ {stats.correct}/{stats.answered}
            {progress ? ` · 🔥 ${progress.streak}` : ''}
          </Typography>
          <Button size="small" onClick={askFinish}>{t('study.finish')}</Button>
        </Stack>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4 }}
          color={pct >= 100 ? 'success' : 'primary'} />
        {progress?.current_lesson && (
          // какой урок сейчас отрабатываем — видно всё занятие, по нажатию открывается грамматика
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.5, cursor: 'pointer' }}
            onClick={() => setGrammarLesson(lessonData(progress.current_lesson.n))}>
            {t('study.currentLesson', { n: progress.current_lesson.n, title: progress.current_lesson.title })}
          </Typography>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {noVoiceAlert}

      {!card && queueEmpty && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>{t('study.allDone')}</Typography>
          {nextDue && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('study.nextReview', { d: new Date(`${nextDue}Z`).toLocaleString(locale) })}
            </Typography>
          )}
          <Stack spacing={1}>
            <Button variant="contained" onClick={() => loadMore([], 5)}>{t('study.moreNew')}</Button>
            <Button onClick={askFinish}>{t('study.finishSession')}</Button>
          </Stack>
        </Paper>
      )}

      {!card && !queueEmpty && <CircularProgress />}

      {showIntro && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Chip size="small" color="secondary" label={t('study.newLesson', { level: lesson.level })} sx={{ mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {t('study.lessonTitle', { n: lesson.n, title: lesson.title })}
          </Typography>
          <MiniMarkdown text={lesson.grammar} translit={course?.target === 'ru' && lesson.n > 0} />
          <AlphabetTable rows={lesson.alphabet} />
          {lesson.verbs.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('study.willConjugate', {
                verbs: lesson.verbs.map(([inf]) => inf).join(', '), tenses: lesson.tense_labels.join(', '),
              })}
            </Typography>
          )}
          <Button variant="contained" fullWidth sx={{ mt: 2 }}
            onClick={() => { touch(); setIntroSeen((s) => new Set(s).add(lesson.n)); }}>
            {t('study.toExercises')}
          </Button>
        </Paper>
      )}

      {card && !showIntro && (
        <Paper sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
            {card.is_new && <Chip size="small" color="secondary" label={t('study.new')} />}
            {card.course === 'personal' && <Chip size="small" label={t('study.mine')} />}
            {lesson && (
              <Chip size="small" variant="outlined" label={t('study.lesson', { n: lesson.n })} onClick={() => setGrammarLesson(lesson)} />
            )}
            {['conj', 'table'].includes(card.type) && <Chip size="small" variant="outlined" label={card.tense_label} />}
            {isRead && <Chip size="small" color="info" label={t('study.readChip')} />}
          </Stack>
          {oldLesson && (
            <Alert severity="warning" icon={false} sx={{ mb: 2, py: 0 }}>
              {card.is_new
                ? t('study.catchUp', { n: card.lesson, cur: currentLessonN })
                : t('study.reviewOld', { n: card.lesson, cur: currentLessonN })}
            </Alert>
          )}

          {card.type === 'table' ? (
            <TableCard key={keyOf(card)} card={card} values={tableAnswers} result={result} firstRef={inputRef} t={t}
              onSpeak={(text) => speak(text, 'it')}
              onChange={(k, v) => { touch(); setTableAnswers((a) => { const n = [...a]; n[k] = v; return n; }); }}
              onSubmit={() => { if (tableAnswers.some((v) => v && v.trim())) submit(); }} />
          ) : card.type === 'aux' ? (
            <AuxCard card={card} result={result} disabled={busy} t={t} onPick={(o) => submit(false, o)} />
          ) : card.type === 'conj' && card.native_prompt ? (
            // главное — фраза на родном языке, которую нужно сказать; лицо, глагол и время — подсказка
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">{t(`study.sayIn.${target}`)}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{card.native_prompt}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                💡 {card.person_label} · {card.infinitive} · {card.tense_label}
              </Typography>
              {showLatin && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }} lang="it">
                  {toLatin(`${card.person_label} · ${card.infinitive}`)}
                </Typography>
              )}
            </Box>
          ) : card.type === 'conj' ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {card.person_label} · {card.infinitive}
              </Typography>
              {showLatin && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }} lang="it">
                  {toLatin(`${card.person_label} · ${card.infinitive}`)}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {card.ru} — {card.tense_label}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography variant={isRead ? 'subtitle1' : 'caption'} color={isRead ? 'text.primary' : 'text.secondary'}
                sx={isRead ? { fontWeight: 600 } : undefined}>
                {promptLabel}
              </Typography>
              <Stack direction="row" sx={{ alignItems: 'center' }}>
                <Typography variant={isRead ? 'h3' : 'h5'} sx={{ fontWeight: 600, my: isRead ? 1 : 0 }}>{card.prompt}</Typography>
                {card.type === 'read' && (
                  // в чтении слово на экране уже по-русски — послушать его до ответа не подсказывает ответ
                  <IconButton size="small" onClick={() => speak(card.prompt, 'ru')} aria-label={t('study.listen')} sx={{ ml: 1 }}>
                    <VolumeUpIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
              {isRead && !result && (
                <Typography variant="body2" color="text.secondary">{t('study.readHint')}</Typography>
              )}
              {card.type === 'drill' && showLatin && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }} lang="it">
                  {toLatin(card.prompt)}
                </Typography>
              )}
              {card.type === 'drill' && card.note && !result && (
                <Typography variant="body2" color="text.secondary">💡 {card.note}</Typography>
              )}
            </Box>
          )}

          {!['table', 'aux'].includes(card.type) && <TextField
            inputRef={inputRef}
            fullWidth
            value={answer}
            onChange={(e) => { setAnswer(e.target.value); touch(); }}
            onKeyDown={onKeyDown}
            placeholder={isRead ? t('study.readPlaceholder') : t('study.placeholder')}
            disabled={!!result}
            autoComplete="off"
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false, lang: target }}
          />}

          {!result ? (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              {card.type !== 'aux' && (
                <Button variant="contained" onClick={() => submit()} sx={{ flex: 1 }}
                  disabled={busy || (card.type === 'table' ? !tableAnswers.some((v) => v && v.trim()) : !answer.trim())}>
                  {t('study.check')}
                </Button>
              )}
              <Button onClick={() => submit(true)} disabled={busy}>{t('study.dontKnow')}</Button>
            </Stack>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Alert severity={verdict.severity} icon={false}
                action={(
                  <IconButton size="small" onClick={() => speak(result.expected, target)} aria-label={t('study.speak')}>
                    <VolumeUpIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <Typography variant="subtitle2">{verdict.title}</Typography>
                {card.type !== 'table' && <Typography variant="h6">{result.expected}</Typography>}
                {showLatin && !isRead && card.type !== 'table' && (
                  <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.85 }} lang="it">
                    {toLatin(result.expected)}
                  </Typography>
                )}
              </Alert>

              {isRead && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">{t('study.letters')}</Typography>
                  <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
                    {lettersOf(card.prompt).map(([cyr, lat], i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Chip key={i} size="small" variant="outlined" label={`${cyr} → ${lat}`} />
                    ))}
                  </Stack>
                </Box>
              )}
              {card.meaning && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('study.meaning', { m: card.meaning })}</Typography>
              )}
              {card.note && card.type !== 'drill' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>💡 {card.note}</Typography>
              )}

              {result.table && (result.verdict !== 'exact' || card.is_new) && (
                <Table size="small" sx={{ mt: 1 }}>
                  <TableBody>
                    {result.table.map(([person, form]) => (
                      <TableRow key={person} selected={person === card.person_label}>
                        <TableCell sx={{ py: 0.5, width: '35%' }}>{person}</TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {form}
                          {showLatin && (
                            <Typography component="span" variant="caption" color="text.secondary"
                              sx={{ fontStyle: 'italic', ml: 1 }} lang="it">
                              {toLatin(form)}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                {!result.correct && result.rating === 1 && answer.trim() && (
                  <Button size="small" onClick={() => rerate(3, t('study.knewItNote'))}>{t('study.knewIt')}</Button>
                )}
                {result.correct && (
                  <>
                    <Button size="small" onClick={() => rerate(2, t('study.wasHardNote'))}>{t('study.wasHard')}</Button>
                    <Button size="small" onClick={() => rerate(4, t('study.tooEasyNote'))}>{t('study.tooEasy')}</Button>
                  </>
                )}
              </Stack>
              {ratingNote && <Typography variant="caption" color="text.secondary">✓ {ratingNote}</Typography>}

              <Button variant="contained" fullWidth sx={{ mt: 2 }} onClick={next}>
                {t('study.next')}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {card && !showIntro && (
        <Typography variant="caption" component="p" sx={{ mt: 1, textAlign: 'right' }}>
          <MuiLink component="button" color="text.secondary" onClick={() => openBugReport({
            card: {
              course: card.course, lesson: card.lesson, type: card.type, skill: card.skill, item_id: card.item_id,
              prompt: card.prompt, expected: result?.expected ?? card.expected_display, answer, verdict: result?.verdict,
            },
          })}>
            {t('bug.cardLink')}
          </MuiLink>
        </Typography>
      )}

      <Dialog open={!!grammarLesson} onClose={() => setGrammarLesson(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('study.lessonTitle', { n: grammarLesson?.n, title: grammarLesson?.title })}</DialogTitle>
        <DialogContent dividers>
          <MiniMarkdown text={grammarLesson?.grammar} translit={course?.target === 'ru' && grammarLesson?.n > 0} />
          <AlphabetTable rows={grammarLesson?.alphabet} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrammarLesson(null)}>{t('study.gotIt')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmFinish} onClose={() => setConfirmFinish(false)}>
        <DialogTitle>{t('study.almostTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('study.almostText', {
              m: Math.max(1, Math.ceil((goalSeconds - todaySeconds) / 60)), goal: Math.round(goalSeconds / 60),
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={finish}>{t('study.finishAnyway')}</Button>
          <Button variant="contained" onClick={() => { setConfirmFinish(false); touch(); }}>{t('study.continue')}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={goalToast} autoHideDuration={5000} onClose={() => setGoalToast(false)}
        message={t('study.goalReached')} />
    </Box>
  );
}
